import { create } from 'zustand';
import { Vec3, type Track } from '@mindcloudxai_3d/core';
import type { RaceKeyframe, RaceTrackEditorState } from './types';
import { captureOrUpdateKeyframe, moveKeyframeTime, optimizeKeyframes } from './keyframeUtils';

interface RaceTrackActions {
  /** 开启/关闭轨道编辑器模式 */
  setEditing: (isEditing: boolean) => void;
  /** 开启/关闭独立预览悬浮控制窗 */
  setPreviewOpen: (open: boolean) => void;
  /** 设置当前是否处于动画轨道沿路播放中 */
  setPlaying: (isPlaying: boolean) => void;
  /** 设置总时间轴时长（默认 8 秒） */
  setDuration: (duration: number) => void;
  /** 设置运镜倍速（默认 1.0） */
  setSpeed: (speed: number) => void;
  /** 设置循环播放方式 */
  setLoopMode: (mode: 'none' | 'repeat' | 'pingpong') => void;
  /** 选择时间轴上特定的关键帧 */
  setSelectedKeyframeId: (id: string | null) => void;

  /**
   * 在当前播放时间捕获当前相机的位姿，作为关键帧。
   * 如果该时间点已存在关键帧则覆盖其姿态，否则进行插入。
   */
  captureCurrentKeyframe: (
    time: number,
    cameraPose: {
      position: { x: number; y: number; z: number };
      target: { x: number; y: number; z: number };
      fov?: number;
      up?: { x: number; y: number; z: number };
    },
  ) => string;

  /** 在指定时间强制新增关键帧，不覆盖已有帧 */
  appendKeyframe: (
    time: number,
    cameraPose: {
      position: { x: number; y: number; z: number };
      target: { x: number; y: number; z: number };
      fov?: number;
      up?: { x: number; y: number; z: number };
    },
  ) => string;

  /**
   * 手动添加或覆盖关键帧
   */
  addOrUpdateKeyframe: (kf: Omit<RaceKeyframe, 'id'>) => string;

  /** 更新关键帧非时间属性（如 position, target, fov, up） */
  updateKeyframePose: (
    id: string,
    updates: Partial<Pick<RaceKeyframe, 'position' | 'target' | 'fov' | 'up'>>,
  ) => void;

  /** 拖拽修改关键帧在时间轴上的秒数 */
  updateKeyframeTime: (id: string, time: number) => void;

  /** 删除某一个特定的关键帧 */
  deleteKeyframe: (id: string) => void;

  /** 清空当前所有的关键帧 */
  clearKeyframes: () => void;

  /** 备份自带的原始 COLMAP 运镜，以便还原 */
  setOriginalTrack: (track: Track | null) => void;

  /** 还原为原始自带运镜状态，丢弃内存编辑 */
  resetToOriginal: () => void;

  /** 设置编辑器内部播放头时间 */
  setEditorTime: (time: number) => void;

  /** 对当前关键帧执行平滑优化（补帧 + 匀速），备份原始帧 */
  optimizeTrack: () => void;

  /** 取消优化，还原用户手动标记的关键帧 */
  restoreTrack: () => void;
}

export type RaceTrackStore = RaceTrackEditorState & RaceTrackActions;

const initialEditorState: RaceTrackEditorState = {
  keyframes: [],
  duration: 8,
  speed: 1.0,
  loopMode: 'repeat',
  selectedKeyframeId: null,
  isEditing: false,
  isPreviewOpen: false,
  isPlaying: false,
  hasEdits: false,
  originalTrack: null,
  editorTime: 0,
  backupKeyframes: null,
};

export const useRaceTrackStore = create<RaceTrackStore>((set, get) => ({
  ...initialEditorState,

  setEditing: (isEditing) => {
    // 退出编辑时，如果未编辑则静默恢复，防止状态遗留
    if (!isEditing && !get().hasEdits) {
      get().resetToOriginal();
    }
    set({ isEditing });
  },

  setPreviewOpen: (isPreviewOpen) => set({ isPreviewOpen }),

  setPlaying: (isPlaying) => set({ isPlaying }),

  setDuration: (duration) => set({ duration }),

  setSpeed: (speed) => set({ speed }),

  setLoopMode: (loopMode) => set({ loopMode }),

  setSelectedKeyframeId: (selectedKeyframeId) => set({ selectedKeyframeId }),

  captureCurrentKeyframe: (time, cameraPose) => {
    const newFrame = {
      time,
      position: new Vec3(cameraPose.position.x, cameraPose.position.y, cameraPose.position.z),
      target: new Vec3(cameraPose.target.x, cameraPose.target.y, cameraPose.target.z),
      fov: cameraPose.fov ?? 60,
      up: cameraPose.up ? new Vec3(cameraPose.up.x, cameraPose.up.y, cameraPose.up.z) : undefined,
    };

    const { keyframes, activeId } = captureOrUpdateKeyframe(get().keyframes, newFrame);
    set({
      keyframes,
      selectedKeyframeId: activeId,
      hasEdits: true,
    });
    return activeId;
  },

  appendKeyframe: (time, cameraPose) => {
    const id = `kf-${Math.random().toString(36).substring(2, 10)}`;
    const keyframes = [
      ...get().keyframes,
      {
        id,
        time,
        position: new Vec3(cameraPose.position.x, cameraPose.position.y, cameraPose.position.z),
        target: new Vec3(cameraPose.target.x, cameraPose.target.y, cameraPose.target.z),
        fov: cameraPose.fov ?? 60,
        up: cameraPose.up ? new Vec3(cameraPose.up.x, cameraPose.up.y, cameraPose.up.z) : undefined,
      },
    ].sort((a, b) => a.time - b.time);
    // 捕获时不自动选中，避免 gizmo 出现在当前相机位置导致遮挡
    set({
      keyframes,
      hasEdits: true,
    });
    return id;
  },

  addOrUpdateKeyframe: (newFrame) => {
    const { keyframes, activeId } = captureOrUpdateKeyframe(get().keyframes, newFrame);
    set({
      keyframes,
      selectedKeyframeId: activeId,
      hasEdits: true,
    });
    return activeId;
  },

  updateKeyframePose: (id, updates) => {
    const keyframes = get().keyframes.map((kf) => {
      if (kf.id !== id) return kf;
      return {
        ...kf,
        ...updates,
      };
    });
    set({ keyframes, hasEdits: true });
  },

  updateKeyframeTime: (id, time) => {
    const keyframes = moveKeyframeTime(get().keyframes, id, time, get().duration);
    set({ keyframes, hasEdits: true });
  },

  deleteKeyframe: (id) => {
    const keyframes = get().keyframes.filter((kf) => kf.id !== id);
    const selectedId = get().selectedKeyframeId === id ? null : get().selectedKeyframeId;
    set({
      keyframes,
      selectedKeyframeId: selectedId,
      hasEdits: true,
    });
  },

  clearKeyframes: () => {
    set({
      keyframes: [],
      selectedKeyframeId: null,
      hasEdits: true,
      backupKeyframes: null,
    });
  },

  setOriginalTrack: (originalTrack) => {
    // 只有在从未备份过时才备份，防止后续更新 editor track 后把 originalTrack 给覆盖了
    if (get().originalTrack === null && originalTrack !== null) {
      set({ originalTrack });
    }
  },

  resetToOriginal: () => {
    set({
      keyframes: [],
      selectedKeyframeId: null,
      hasEdits: false,
      isPlaying: false,
      editorTime: 0,
    });
  },

  setEditorTime: (editorTime) => set({ editorTime }),

  optimizeTrack: () => {
    const { keyframes, duration, backupKeyframes } = get();
    if (keyframes.length < 2) return;
    // 已有备份说明是再次优化，不覆盖原始备份
    const backup = backupKeyframes ?? keyframes.map((kf) => ({ ...kf }));
    const optimized = optimizeKeyframes(keyframes, duration);
    set({
      keyframes: optimized,
      selectedKeyframeId: null,
      hasEdits: true,
      backupKeyframes: backup,
      isPlaying: false,
    });
  },

  restoreTrack: () => {
    const { backupKeyframes } = get();
    if (!backupKeyframes) return;
    set({
      keyframes: backupKeyframes.map((kf) => ({ ...kf })),
      selectedKeyframeId: null,
      backupKeyframes: null,
      isPlaying: false,
    });
  },
}));
